import { useState, useRef, useCallback, useEffect } from 'react';

// Web Speech API types — not available in all TS configs
interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  onstart: ((this: SpeechRecognitionInstance, ev: Event) => void) | null;
  onend: ((this: SpeechRecognitionInstance, ev: Event) => void) | null;
  onerror: ((this: SpeechRecognitionInstance, ev: any) => void) | null;
  onresult: ((this: SpeechRecognitionInstance, ev: any) => void) | null;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognitionInstance;
}

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

const SpeechRecognitionCtor: SpeechRecognitionConstructor | undefined =
  typeof window !== 'undefined'
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : undefined;

/** Whether the current browser supports the Web Speech API. */
export const isSpeechRecognitionSupported = !!SpeechRecognitionCtor;

/**
 * Hook that exposes browser-native speech-to-text.
 *
 * Returns `{ isListening, toggle, stop }`.
 * Call `toggle()` to start/stop. Transcript is written into `onTranscript`.
 */
export function useSpeechRecognition(onTranscript: (text: string) => void) {
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;

  const stop = useCallback(() => {
    if (recognitionRef.current) {
      // Detach onresult before stopping so the final result event doesn't
      // write the transcript back (e.g., after handleSend clears the input).
      recognitionRef.current.onresult = null;
      recognitionRef.current.stop();
    }
  }, []);

  const toggle = useCallback(() => {
    if (!SpeechRecognitionCtor) return;

    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }

    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognitionRef.current = recognition;

    let finalizedText = '';

    recognition.onstart = () => setIsListening(true);

    recognition.onresult = (event: any) => {
      let interim = '';
      for (let i = 0; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalizedText += transcript;
        } else {
          interim += transcript;
        }
      }
      const text = (finalizedText + interim).trim();
      if (text) {
        onTranscriptRef.current(text);
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognition.onerror = (event: any) => {
      console.debug('Speech recognition error:', event.error);
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognition.start();
  }, [isListening]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      recognitionRef.current?.stop();
    };
  }, []);

  return { isListening, toggle, stop };
}
